import { useEffect, useRef } from 'react';
import { subscribe } from '@/lib/services/ws.service.js';
import { hydrationManager } from '@/lib/services/reference-hydration.js';
import { applyReferenceInvalidation } from '@/lib/services/reference-invalidation.js';
import { referenceBroadcast } from '@/lib/services/reference-broadcast.js';
import { subscribeTenantChange, useTenantStore } from '@/store/tenant.store.js';
import { useReferenceStore, REFERENCE_ENTITIES } from '@/store/reference.store.js';
import { useAuthStore } from '@/store/auth.store.js';
import {
    getTenantRolePermissionSnapshot,
    tenantSnapshotHasAnyUsablePermission,
} from '@/lib/tenant-role-permissions.js';
import { getHydratableReferenceEntities } from '@/lib/reference-hydrate-filter.js';
import { isPersistedTenantMatch } from '@/lib/services/reference-tenant-cache.js';
import { sdk } from '@/lib/api/client.js';
import { createIntegrationLogger } from '@/lib/app-log.js';
import { logReferenceDetailSync } from '@/lib/reference-sync/reference-detail-sync-debug.js';

const POLL_INTERVAL_MS = 60_000;
const TENANT_HYDRATE_DELAY_MS = 150;
const referenceSyncLog = createIntegrationLogger('reference-sync');

function waitForReferencePersistHydration() {
    const api = useReferenceStore.persist;
    if (!api) return Promise.resolve();
    if (api.hasHydrated?.()) return Promise.resolve();
    return new Promise((resolve) => {
        const unsub = api.onFinishHydration?.(() => {
            unsub?.();
            resolve();
        });
        // If already finished between hasHydrated and subscribe
        if (api.hasHydrated?.()) {
            unsub?.();
            resolve();
        }
    });
}

function shouldForceHydrateForTenant() {
    const selectedTenantId = useTenantStore.getState().selectedTenantId;
    const { persistedTenantId, entities, lastHydratedAt } = useReferenceStore.getState();
    if (!isPersistedTenantMatch(persistedTenantId, selectedTenantId)) return true;
    if (!lastHydratedAt) return true;
    const hasAnyEntity = Object.values(entities || {}).some(
        (list) => Array.isArray(list) && list.length > 0,
    );
    return !hasAnyEntity;
}

export function useReferenceSync() {
    const pollRef = useRef(null);
    const socketAliveRef = useRef(true);
    const tenantHydrateTimerRef = useRef(null);
    /** Avoid spamming hydrateAll on every store tick; reset on tenant change / logout. */
    const didHydrateForEligibilityRef = useRef(false);

    useEffect(() => {
        const canHydrateReference = () => {
            const isHydrated = useAuthStore.persist?.hasHydrated?.();
            const { token, user } = useAuthStore.getState();
            const tenantId = useTenantStore.getState().selectedTenantId;
            if (!isHydrated || !token || !tenantId || !user) return false;
            const snapshot = getTenantRolePermissionSnapshot(user, tenantId);
            return tenantSnapshotHasAnyUsablePermission(snapshot);
        };

        const bulkEntitiesForHydration = () => {
            const { token, user } = useAuthStore.getState();
            const tenantId = useTenantStore.getState().selectedTenantId;
            if (!token || !user || !tenantId) return undefined;
            const snapshot = getTenantRolePermissionSnapshot(user, tenantId);
            return getHydratableReferenceEntities(snapshot);
        };

        const runDevRegistryContractCheck = async () => {
            if (!import.meta.env.DEV) return;
            try {
                const res = await sdk.referenceData.getRegistry();
                // `data` is the unfiltered catalog; do not compare against RBAC `accessible`.
                const server = new Set(res?.data || []);
                const missingOnServer = REFERENCE_ENTITIES.filter((k) => !server.has(k));
                const extraOnServer = [...server].filter((k) => !REFERENCE_ENTITIES.includes(k));
                if (missingOnServer.length > 0 || extraOnServer.length > 0) {
                    referenceSyncLog.warn('[ReferenceSync] Registry mismatch', { missingOnServer, extraOnServer });
                }
            } catch (e) {
                referenceSyncLog.warn('[ReferenceSync] Registry check failed', { error: e?.message });
            }
        };

        let lastBulkKey = '';

        const tryHydrateWhenNewlyEligible = async () => {
            if (!canHydrateReference()) {
                didHydrateForEligibilityRef.current = false;
                lastBulkKey = '';
                return;
            }

            const bulk = bulkEntitiesForHydration();
            const bulkKey = (bulk || []).slice().sort().join(',');
            // Permission expansion mid-session: new page deps must re-hydrate.
            if (didHydrateForEligibilityRef.current && bulkKey && bulkKey !== lastBulkKey) {
                didHydrateForEligibilityRef.current = false;
            }

            if (didHydrateForEligibilityRef.current) return;
            didHydrateForEligibilityRef.current = true;
            lastBulkKey = bulkKey;

            await waitForReferencePersistHydration();
            if (!canHydrateReference()) {
                didHydrateForEligibilityRef.current = false;
                lastBulkKey = '';
                return;
            }

            const force = shouldForceHydrateForTenant();
            hydrationManager.hydrateAll({ force, bulkEntities: bulkEntitiesForHydration() });
            void runDevRegistryContractCheck();
        };

        const scheduleForceHydrateAfterTenantChange = () => {
            if (tenantHydrateTimerRef.current) {
                clearTimeout(tenantHydrateTimerRef.current);
                tenantHydrateTimerRef.current = null;
            }
            tenantHydrateTimerRef.current = setTimeout(() => {
                tenantHydrateTimerRef.current = null;
                const isHydrated = useAuthStore.persist?.hasHydrated?.();
                const { token, isLoggingOut } = useAuthStore.getState();
                if (isLoggingOut || !token || !isHydrated) return;
                if (!canHydrateReference()) return;
                didHydrateForEligibilityRef.current = true;
                hydrationManager.hydrateAll({
                    force: true,
                    bulkEntities: bulkEntitiesForHydration(),
                });
            }, TENANT_HYDRATE_DELAY_MS);
        };

        referenceBroadcast.init();
        hydrationManager.setBroadcastFn((msg) => referenceBroadcast.send(msg));

        void tryHydrateWhenNewlyEligible();

        const unsubAuth = useAuthStore.subscribe(() => {
            void tryHydrateWhenNewlyEligible();
        });

        const unsubRef = subscribe('reference_invalidated', (payload) => {
            if (payload?.entity) {
                referenceSyncLog.debug('reference_invalidated received', {
                    entity: payload.entity,
                    action: payload.action,
                    version: payload.version,
                    timestamp: payload.timestamp,
                });
                logReferenceDetailSync('socket', 'reference_invalidated received', {
                    entity: payload.entity,
                    action: payload.action,
                    version: payload.version,
                    timestamp: payload.timestamp,
                });
                applyReferenceInvalidation(payload.entity, payload.version);
            }
        });

        const unsubDisconnect = subscribe('disconnect', () => {
            socketAliveRef.current = false;
            startPolling();
        });

        const unsubReconnect = subscribe('connect', () => {
            socketAliveRef.current = true;
            stopPolling();
            if (canHydrateReference()) {
                hydrationManager.hydrateAll({ bulkEntities: bulkEntitiesForHydration() });
            }
        });

        const unsubTenant = subscribeTenantChange((nextTenantId, _prevTenantId, meta = {}) => {
            didHydrateForEligibilityRef.current = false;
            hydrationManager.resetForTenantChange();
            useReferenceStore.getState().reset();
            // Originating tab notifies peers; broadcast-driven sync must not ping-pong.
            if (!meta.fromBroadcast) {
                referenceBroadcast.notifyTenantReset(nextTenantId);
            }
            scheduleForceHydrateAfterTenantChange();
        });

        function startPolling() {
            if (pollRef.current) return;
            pollRef.current = setInterval(() => {
                if (canHydrateReference()) {
                    hydrationManager.hydrateAll({ bulkEntities: bulkEntitiesForHydration() });
                }
            }, POLL_INTERVAL_MS);
        }

        function stopPolling() {
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
        }

        return () => {
            unsubAuth();
            unsubRef();
            unsubDisconnect();
            unsubReconnect();
            unsubTenant();
            stopPolling();
            if (tenantHydrateTimerRef.current) {
                clearTimeout(tenantHydrateTimerRef.current);
                tenantHydrateTimerRef.current = null;
            }
        };
    }, []);
}
